import LinkCTA from '2-core-elements/LinkCTA/LinkCTA';
import DataList from '6-my-mcc/Components/DataList/DataList';
import { useMemo, useState, type FC } from 'react';
import FormMyTeams from './FormMyTeams';
import type { ProfileDetail } from '../../Utils/useProfileQuery';
import type { SupportTeam } from '../../Utils/useSupportTeamsQuery';

interface MyTeamsProps {
    profileDetail: ProfileDetail;
    supportTeams: {
        aflTeams: SupportTeam[];
        bblTeams: SupportTeam[];
    };
}

const MyTeams: FC<MyTeamsProps> = ({ profileDetail, supportTeams }) => {
    const [showEditForm, setShowEditForm] = useState<boolean>(false);

    const teams = useMemo((): {
        afl: { name?: string | null; logo?: string | null };
        bbl: { name?: string | null; logo?: string | null };
    } => {
        // find the team details from the supportTeams list based on the profileDetail
        // if team not found, return undefined for name and logo
        // this can happen if the user has a team that is no longer in the list,
        // or the user team data is not listed in the supportTeams
        const afl = supportTeams.aflTeams.find(team => {
            return team.name === profileDetail.aflTeamOfSupport || team.displayName === profileDetail.aflTeamOfSupport;
        });
        const bbl = supportTeams.bblTeams.find(team => {
            return team.name === profileDetail.bblTeamOfSupport || team.displayName === profileDetail.bblTeamOfSupport;
        });

        return {
            afl: { name: afl?.displayName, logo: afl?.logo },
            bbl: { name: bbl?.displayName, logo: bbl?.logo },
        };
    }, [profileDetail, supportTeams]);

    return (
        <div className="profile-panel">
            <div className="profile-panel-header base-wrapper-header">
                <h1 className="base-wrapper-title">My Teams</h1>
                <div className="profile-panel-tools">
                    {showEditForm ? (
                        <LinkCTA
                            size={'sm'}
                            suffixIcon={'close'}
                            onlyIcon={true}
                            aria-label="Cancel edit"
                            type="button"
                            onClick={(): void => {
                                setShowEditForm(false);
                            }}
                        />
                    ) : (
                        <LinkCTA
                            size={'xs'}
                            suffixIcon={'edit'}
                            aria-label="Edit team preferences"
                            type="button"
                            onClick={(): void => {
                                setShowEditForm(true);
                            }}
                        >
                            Manage
                        </LinkCTA>
                    )}
                </div>
            </div>

            {showEditForm && (
                <FormMyTeams
                    profileDetail={profileDetail}
                    supportTeams={supportTeams}
                    onSubmitSuccess={(): void => {
                        setShowEditForm(false);
                    }}
                />
            )}

            {!showEditForm && (
                <DataList variant="team">
                    <DataList.Team label="AFL" value={teams.afl.name} logo={teams.afl.logo} />
                    <DataList.Team label="BBL" value={teams.bbl.name} logo={teams.bbl.logo} />
                </DataList>
            )}
        </div>
    );
};

export default MyTeams;
